import { format, parse, parseISO } from 'date-fns';
import _flatMap from 'lodash/flatMap';
import _isEmpty from 'lodash/isEmpty';
import { FULL_ISO_DATE_FORMAT } from 'constants/logic';
import { transformKeysToSnakeCase, renameKeys } from 'helpers/core';
import { AVAILABILITY_TYPES } from './ManageServices.constants';

const transformSingleDayException = data => {
  const { date, startTime, endTime, availability, notEditableType, ...restData } = data;
  return {
    start_datetime: format(parse(startTime, 'HH:mm', parseISO(date)), FULL_ISO_DATE_FORMAT),
    end_datetime: format(parse(endTime, 'HH:mm', parseISO(date)), FULL_ISO_DATE_FORMAT),
    ...restData,
  };
};

export const transformAvailabilityDataToAPIShape = data => {
  let transformedData = { availabilityRecurrence: [] };
  if (data.recurrence) {
    transformedData.availabilityRecurrence = _flatMap(data.recurrence, (value, key) =>
      value.map(element => ({ weekday: key, ...element }))
    );
  }

  if (data.singleDayExceptions) {
    transformedData.availabilityOverrides = data.singleDayExceptions
      .filter(({ availability }) => availability === AVAILABILITY_TYPES.AVAILABLE)
      .map(transformSingleDayException)
      .map(({ description, ...restData }) => ({ ...restData }));

    transformedData.time_blocks = data.singleDayExceptions
      .filter(({ availability }) => availability === AVAILABILITY_TYPES.UNAVAILABLE)
      .map(transformSingleDayException);
  }

  transformedData = transformKeysToSnakeCase({ ...data, ...transformedData });

  return renameKeys(transformedData, { delete: '_delete' });
};

export const transformAvailabilityRecurrence = recurrence => {
  const transformed = {};
  recurrence.forEach(item => {
    const { weekday, ...restRecurrenceData } = item;
    const { startTime, endTime } = restRecurrenceData;
    const trimmedStartTime = startTime && startTime.slice(0, -3);
    const trimmedEndTime = endTime && endTime.slice(0, -3);
    const timeRange = { startTime: trimmedStartTime, endTime: trimmedEndTime };

    (transformed[weekday] = transformed[weekday] ? transformed[weekday] : []).push(timeRange);
  });

  return transformed;
};

export const transformAvailabilityDataToEntityShape = data =>
  data.map(item => {
    const { availabilityRecurrence, availabilityOverrides, timeBlocks, ...restItem } = item;
    const transformedAvailabilityData = {};
    let singleDayExceptions = [];

    if (availabilityRecurrence) {
      transformedAvailabilityData.recurrence = transformAvailabilityRecurrence(availabilityRecurrence);
    }

    if (availabilityOverrides)
      singleDayExceptions = singleDayExceptions.concat(
        availabilityOverrides.map(value => ({
          availability: AVAILABILITY_TYPES.AVAILABLE,
          notEditableType: true,
          ...value,
        }))
      );

    if (timeBlocks)
      singleDayExceptions = singleDayExceptions.concat(
        timeBlocks.map(value => ({ availability: AVAILABILITY_TYPES.UNAVAILABLE, notEditableType: true, ...value }))
      );

    if (singleDayExceptions) {
      const sortedSingleDayExceptions = singleDayExceptions.sort(
        ({ startDatetime: firstStartDatetime }, { startDatetime: secondStartDatetime }) =>
          parseISO(firstStartDatetime) - parseISO(secondStartDatetime)
      );
      singleDayExceptions = sortedSingleDayExceptions.map(availability => {
        const { startDatetime, endDatetime, ...restAvailability } = availability;

        return {
          startTime: format(parseISO(startDatetime), 'HH:mm'),
          endTime: format(parseISO(endDatetime), 'HH:mm'),
          date: startDatetime,
          ...restAvailability,
        };
      });
    }

    if (!_isEmpty(singleDayExceptions)) transformedAvailabilityData.singleDayExceptions = singleDayExceptions;

    return { ...restItem, ...transformedAvailabilityData };
  });
